import { useTranslation } from "@ferrivia/i18n";
import { trendDelta, trendDirection, type TrendDirection } from "./direction";
import s from "./ResultTrend.module.css";

/* ---------------------------------------------------------------------- *
 * DELIBERATE DEVIATION FROM REQ-MDR-002 — authorised by Ville, 2026-09-05,
 * extended to the questionnaire summary on 2026-10-07 and 2026-10-09.
 *
 * REQ-MDR-002 and the root CLAUDE.md both say the software must not derive
 * or trend a client's measured values, and compliance/roadmap.md's charting
 * row permits a trend only with "no interpretation, colour, or directional
 * language attached". This component is all of it: it subtracts two values,
 * states a direction, prints the magnitude, and colours the result.
 *
 * It is deliberately the whole of that deviation — nothing else lives here,
 * so the day the decision is revisited this directory is deleted and its
 * call sites drop one element. The call sites are the home card, the lab
 * rows of the questionnaire summary (2026-10-07), and the summary's
 * energy/mood change (kind "checkin", 2026-10-09). Do not spread the pattern
 * to anything else: anything that wants to say something about a client's
 * value belongs in a clinician's authored words, not in another component
 * like this one.
 *
 * Recorded in compliance/roadmap.md ("Show the direction and size of change
 * between a client's consecutive measured values, in colour") and in
 * REQ-MDR-002's notes. It also trips REQ-MDR-001: the device qualification
 * determination has to be re-taken against it.
 * ---------------------------------------------------------------------- */

/* Plain text arrows, never emoji (Ville, 2026-09-05). U+2197 ↗ and U+2198 ↘
   are both in the Unicode emoji set, so iOS renders them as colour emoji
   glyphs that ignore this element's own colour and sit wrong on the
   baseline. U+2191 ↑, U+2193 ↓ and U+2192 → are not, so they render as text
   in the page's own typeface, inherit currentColor, and scale with the font
   size. Any replacement must come from outside the emoji set — or be a
   lucide-react icon, which this app already depends on. */
const GLYPH: Record<TrendDirection, string> = { higher: "\u2191", lower: "\u2193", unchanged: "\u2192" };

/* Measured values are printed as they came from the lab everywhere in this
   app — LatestResultBlock's own value above this element, and every row on
   ResultsScreen — rather than run through Intl. So is this, or the same line
   would carry two number conventions ("128.45 µg/L ↗ +32,25 µg/L" under a
   Finnish locale). Whether measured numbers should be locale-formatted app-wide
   is a real question, and a bigger one than this element.

   Two fraction digits is the most any analyte's reference range is quoted to
   (LAB_ANALYTE_REFERENCES), so it is the point past which further digits are
   the subtraction's floating-point tail rather than anything measured. */
function formatMagnitude(value: number): string {
  return String(Math.round(Math.abs(value) * 100) / 100);
}

/** U+2212, not a hyphen — it is the right character beside a measurement. */
const SIGN: Record<"higher" | "lower", string> = { higher: "+", lower: "\u2212" };

export interface ResultTrendProps {
  current: number;
  previous: number;
  /** The analyte's own unit, so the change is stated in the same terms as
      the value it is a change in. */
  unit: string;
  /** "checkin" is a self-reported slider moved between two summaries, not a
      measurement, so only the spoken sentence differs. */
  kind?: "measurement" | "checkin";
}

export function ResultTrend({ current, previous, unit, kind = "measurement" }: ResultTrendProps) {
  const { t } = useTranslation("client");

  const direction = trendDirection(current, previous);
  const delta = trendDelta(current, previous);

  // role="img" + aria-label replaces the whole content for assistive
  // technology, which is what this needs: the arrow announces as nothing on
  // its own, and "↗ +7 µg/L" read literally is worse than a sentence. Colour
  // is therefore never the only carrier of the meaning (WCAG 1.4.1) — the
  // glyph carries it visually and the label carries it in words.
  if (direction === "unchanged") {
    const label =
      kind === "checkin"
        ? t("summary.checkinTrend.unchanged", "Sama kuin edellisellä kerralla")
        : t("results.trend.unchanged", "Sama kuin edellinen mittaus");
    return (
      <span className={`${s.root} ${s.unchanged}`} role="img" aria-label={label}>
        {GLYPH.unchanged}
      </span>
    );
  }

  const magnitude = `${formatMagnitude(delta)} ${unit}`;
  const label =
    kind === "checkin"
      ? direction === "higher"
        ? t("summary.checkinTrend.higher", "{{delta}} prosenttiyksikköä enemmän kuin edellisellä kerralla", {
            delta: formatMagnitude(delta),
          })
        : t("summary.checkinTrend.lower", "{{delta}} prosenttiyksikköä vähemmän kuin edellisellä kerralla", {
            delta: formatMagnitude(delta),
          })
      : direction === "higher"
        ? t("results.trend.higher", "{{delta}} suurempi kuin edellinen mittaus", { delta: magnitude })
        : t("results.trend.lower", "{{delta}} pienempi kuin edellinen mittaus", { delta: magnitude });

  return (
    <span className={`${s.root} ${s[direction]}`} role="img" aria-label={label}>
      {GLYPH[direction]} {SIGN[direction]}
      {magnitude}
    </span>
  );
}
